
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { GroupMember } from "@/lib/types";
import { toast } from "sonner";
import { safeMaybeSingle } from "@/utils/supabase/queries";
import { toDatabaseValue, isDatabaseRecord } from "@/utils/supabase/type-guards";

export function useMember(groupId: string | undefined, userId: string | undefined) {
  const [member, setMember] = useState<GroupMember | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!groupId || !userId) {
      setLoading(false);
      return;
    }

    const fetchMember = async () => {
      try {
        const memberData = await safeMaybeSingle<any>(
          supabase
            .from("group_members")
            .select("*")
            .eq("group_id", toDatabaseValue(groupId))
            .eq("user_id", toDatabaseValue(userId)),
          "Failed to load member"
        );

        if (memberData && isDatabaseRecord(memberData)) {
          const memberInfo: GroupMember = {
            id: memberData.id,
            groupId: memberData.group_id,
            userId: memberData.user_id,
            role: memberData.role,
            isBanned: memberData.is_banned,
            isMuted: memberData.is_muted,
            mutedUntil: memberData.muted_until,
            joinedAt: memberData.joined_at,
          };
          setMember(memberInfo);
        } else {
          setMember(null);
        }
      } catch (error) {
        console.error("Error fetching member:", error);
        toast.error("Failed to load member details");
        setMember(null);
      } finally {
        setLoading(false);
      }
    };

    fetchMember();
  }, [groupId, userId]);

  return {
    member,
    loading,
  };
}
